import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, Globe } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const GREENSTITCH_LOGO = 'https://greenstitch.io/wp-content/uploads/2025/05/cropped-greenstitch-logo-1.webp';
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingUsers, setCheckingUsers] = useState(true);
  const [isBootstrap, setIsBootstrap] = useState(false);
  const { login, bootstrap } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    checkIfBootstrapNeeded();
  }, []);

  const checkIfBootstrapNeeded = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/count`);
      setIsBootstrap(response.data.count === 0);
    } catch (error) {
      // If error (likely auth required), assume users exist
      setIsBootstrap(false);
    } finally {
      setCheckingUsers(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isBootstrap) {
        await bootstrap(email, password, name);
        toast.success('Admin account created! Welcome to Atlas.');
      } else {
        await login(email, password);
        toast.success('Welcome back!');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1604935067269-27c7e8b36618?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMG1pbmltYWxpc3QlMjBuYXR1cmUlMjBnZW9tZXRyaWN8ZW58MHx8fHwxNzY4MjgwODUyfDA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-primary/60" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <Globe className="w-7 h-7 text-white" />
            </div>
            <span className="font-heading font-bold text-3xl">Atlas</span>
          </div>
          <h1 className="font-heading text-4xl font-bold mb-4">
            Emission Factor Management System
          </h1>
          <p className="text-lg text-white/80">
            Efficiently manage, track, and approve emission factors with complete audit trails.
          </p>
        </div>
      </div>

      {/* Right panel - Login form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-2 mb-8">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-bold text-2xl text-gray-900">Atlas</span>
          </div>

          <Card className="border-gray-200 shadow-sm animate-fade-in">
            <CardHeader className="space-y-1">
              <CardTitle className="font-heading text-2xl">
                {isBootstrap ? 'Create Admin Account' : 'Sign in'}
              </CardTitle>
              <CardDescription>
                {isBootstrap 
                  ? 'Set up your first admin account to get started'
                  : 'Enter your credentials to access the dashboard'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {checkingUsers ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {isBootstrap && (
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name</Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="Admin User"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        className="input-eco"
                        data-testid="bootstrap-name-input"
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="input-eco"
                      data-testid="login-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Password</Label>
                      {!isBootstrap && (
                        <Link 
                          to="/forgot-password" 
                          className="text-xs text-primary hover:underline"
                          data-testid="forgot-password-link"
                        >
                          Forgot password?
                        </Link>
                      )}
                    </div>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="input-eco"
                      data-testid="login-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full btn-primary"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {isBootstrap ? 'Creating account...' : 'Signing in...'}
                      </>
                    ) : (
                      isBootstrap ? 'Create Admin Account' : 'Sign in'
                    )}
                  </Button>
                </form>
              )}

              {!isBootstrap && !checkingUsers && (
                <div className="mt-6 text-center text-xs text-muted-foreground">
                  Contact your administrator if you need an account
                </div>
              )}
            </CardContent>
          </Card>

          {/* Footer branding */}
          <div className="mt-8 text-center">
            <p className="text-xs text-muted-foreground mb-2">Powered by</p>
            <img src={GREENSTITCH_LOGO} alt="GreenStitch.io" className="h-6 mx-auto opacity-70" />
          </div>
        </div>
      </div>
    </div>
  );
}
